import type { AjaxAction } from '../_actions/AjaxAction';
import type { AppDispatch, RootState } from '../../WebsiteStore';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';

type RequestId<ResponseData, Data> = string | ((action: AjaxAction<ResponseData>, data: Data) => string);

type UseSuspenseDataOptions<Data, ResponseData> = {
	select: (state: RootState) => Data
	isReady?: (data: Data) => boolean
	load: (data: Data) => AjaxAction<ResponseData> | undefined
	requestId?: RequestId<ResponseData, Data>
}

const requests = new Map<string, Promise<void>>();

const serializeActionData = (data: unknown): string => {
	if (!data) {
		return '';
	}

	const params = new URLSearchParams();

	if (typeof FormData !== 'undefined' && data instanceof FormData) {
		data.forEach((value, key) => {
			params.append(key, String(value));
		});

		return params.toString();
	}

	Object.entries(data).forEach(([key, value]) => {
		params.append(key, String(value));
	});

	return params.toString();
}

const getActionRequestId = <ResponseData, Data>(
	action: AjaxAction<ResponseData>,
	data: Data,
	requestId?: RequestId<ResponseData, Data>
) => {
	if (typeof requestId === 'function') {
		return requestId(action, data);
	}

	if (requestId) {
		return requestId;
	}

	return [
		action.actionType,
		action.method ?? 'GET',
		action.url,
		serializeActionData(action.data),
	].join(':');
}

const loadData = <ResponseData>(
	dispatch: AppDispatch,
	action: AjaxAction<ResponseData>,
	requestId: string
) => {
	const request = requests.get(requestId);

	if (request) {
		return request;
	}

	let pendingRequest: Promise<void> = new Promise((resolve, reject) => {
		const suspenseAction = action.set({
			onSuccess: () => resolve(),
			onFailure: response => reject(response),
			onAbort: response => reject(response),
		});

		dispatch(suspenseAction as unknown as Parameters<AppDispatch>[0]);
	});

	pendingRequest = pendingRequest.finally(() => {
		if (requests.get(requestId) === pendingRequest) {
			requests.delete(requestId);
		}
	});

	requests.set(requestId, pendingRequest);

	return pendingRequest;
}

export default function useSuspenseData<Data, ResponseData = unknown>({
	select,
	isReady = data => data !== undefined && data !== null,
	load,
	requestId,
}: UseSuspenseDataOptions<Data, ResponseData>): Data {
	const dispatch = useAppDispatch();
	const data = useAppSelector(select);

	if (isReady(data)) {
		return data;
	}

	const action = load(data);

	if (!action) {
		return data;
	}

	throw loadData(
		dispatch,
		action,
		getActionRequestId(action, data, requestId)
	);
}
